/**
 * @description This file contains the shared React contexts used by both
 * automation-module-registry and automation-templates-module-registry.
 * By sharing the context instances, components can consume the context
 * regardless of which registry provides it.
 */

import * as React from 'react';
import type {GenericObject} from 'src/types/sense';

// ============================================================================
// Type Definitions
// ============================================================================

export type SimpleABTVariable = {
  title: string;
  show_on_ui: boolean;
  type: string;
  key: string;
  entity_type: string;
  description: string;
  possible_values: Array<boolean>;
  fieldPath?: string;
  fieldGroup?: string;
  moduleKey: string;
  moduleName: string | null | undefined;
  prevNodeVariableType: 'abt';
  show_on_content: boolean;
  show_on_settings: boolean;
  show_on_logicalnodes: boolean;
  node_name?: string;
  display_name?: string;
};

export type SimpleVariable = {
  origin?: 'TEMPLATE' | 'ORIGINAL';
  title: string;
  attribute_name?: string;
  type: string;
  entity_type: string;
  relations?: Array<string>;
  entity_type_display_name: string;
  show_on_content: boolean;
  show_on_settings: boolean;
  show_on_logicalnodes: boolean;
  emsGroup: string;
  is_global_variable: boolean;
  show_on_ui: boolean;
  minimum?: number;
  maximum?: number;
};

export type ReqResVariable = {
  title: string;
  type: string;
  entity_type?: string;
  entity_type_display_name?: string;
  show_on_content: boolean;
  show_on_settings: boolean;
  show_on_logicalnodes: boolean;
  show_on_ui: boolean;
  moduleKey: string;
  moduleName: string | null | undefined;
  prevNodeVariableType: 'reqRes';
  node_name: string;
  display_name: string;
  nodeId: string;
  taskId: string;
  description: string;
  items_schema?: {
    type: string;
    enum?: Array<any>;
    [key: string]: any;
  };
  selectionLevelPath: string;
  selectionLevelPathId: string;
  selectionLevelTitle: string;
  selection_text: string;
  enum?: Array<string>;
  minimum?: number;
  maximum?: number;
  not_used_in_task_group?: boolean;
};

export type ABTUtilVariables = Map<
  string,
  Map<string, Map<string, SimpleABTVariable>>
>;

export type ABTVariables = {
  [key: string]:
    | {
        pickerVariables: Map<string, SimpleABTVariable>;
        utilVariables: ABTUtilVariables;
      }
    | null
    | undefined;
};

export type ReqResVariables = Map<
  string,
  Map<string, Map<string, ReqResVariable>>
>;

export type AutomationVariables<T> = {
  ems: Map<string, T>;
  abt: ABTVariables;
  reqRes: ReqResVariables;
  allowedActionEvents?: {
    [key: string]: Map<string, Map<string, Map<string, SimpleABTVariable>>>;
  };
  allowedReqResVariables?: Map<string, Map<string, ReqResVariable>>;
};

export type DynamicTaskObjectDetails = {
  id: string;
  node_name: string;
  display_name: string;
  task_group_type?: string;
  previous_node_context:
    | {
        key: string;
      }
    | null
    | undefined;
  anchor_entity_type: string;
  trigger_entity_type: string;
};

export type DynamicTaskObjectModuleDetails = {
  type: string;
  params: GenericObject;
  display_name: string;
  response_schema_key: string;
  not_used_in_task_group: boolean;
};

export type DynamicTaskObjectModules = {
  [key: string]: DynamicTaskObjectModuleDetails;
};

export type DynamicTaskObject = {
  details: DynamicTaskObjectDetails;
  modules: DynamicTaskObjectModules;
};

export type DynamicResponse = {
  [key: string]: DynamicTaskObject;
};

export type AutomationModulesContext<T> = {
  automationVariables: AutomationVariables<T>;
  previousNodeInfo: DynamicResponse | null | undefined;
  getStaticAutomationModule: (arg1: string) => GenericObject;
  getJSONPathMapForTaskGroup: (
    arg1: string,
    arg2?: string | null | undefined,
  ) => Map<string, any>;
  dynamicResponseSchema:
    | {
        [key: string]: DynamicResponse;
      }
    | null
    | undefined;
  getPreviousNodeInfo: (arg1: string) => DynamicResponse | null | undefined;
  isLoading: boolean;
  getModuleVariables: (
    nodeId: string,
  ) => Map<string, string> | null | undefined;
};

// ============================================================================
// Shared Context Instances
// ============================================================================

// NOTE:(diwakarsurya): there is no other way to define a context
// which supports generic type we need to use `any` which is a
// supertype of all in this case. see
// https://stackoverflow.com/questions/60725621/react-context-with-generics
// for more info

// @ts-expect-error - TS2322 - Type 'Context<{ automationVariables: { ems: Map<any, any>; abt: {}; reqRes: Map<any, any>; allowedActionEvents: {}; allowedReqResVariables: Map<any, any>; }; previousNodeInfo: {}; getStaticAutomationModule: () => {}; ... 4 more ...; getModuleVariables: () => Map<...>; }>' is not assignable to type 'Context<AutomationModulesContext<any>>'.
export const AutomationModulesRegistryContext: React.Context<
  AutomationModulesContext<any>
> = React.createContext({
  automationVariables: {
    ems: new Map(),
    abt: {},
    reqRes: new Map(),
    allowedActionEvents: {},
    allowedReqResVariables: new Map(),
  },
  previousNodeInfo: {},
  getStaticAutomationModule: () => ({}),
  getJSONPathMapForTaskGroup: () => {
    return new Map();
  },
  dynamicResponseSchema: {},
  getPreviousNodeInfo: () => null,
  isLoading: false,
  getModuleVariables: () => new Map(),
});

export const VariableListMapContext: React.Context<{
  ems: Map<string, SimpleVariable>;
  reqRes: Map<string, ReqResVariable>;
  isLoading: boolean;
} | null> = React.createContext<{
  ems: Map<string, SimpleVariable>;
  reqRes: Map<string, ReqResVariable>;
  isLoading: boolean;
} | null>(null);
